import { API } from '../../api';
import { Button, Col, Dropdown, PageHeader, Row, Table, Tag } from 'antd';
import { DimaContext } from '../../DimaContext';
import { DownOutlined } from '@ant-design/icons';
import { DropdownMenu } from '../editableTable/dropdownMenu';
import { checkPermission, handleError } from '../../utils/util';
import CreateValidationRulesModal from './createValidationRulesModal';
import React, { useContext, useEffect, useState } from 'react';

const ValidationRules = () => {
  const context = useContext(DimaContext);
  // context options are hardcoded as ENUM in DB
  const contextOptions = ['r4', 'carin-bb', 'us-core'];
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [resourceTypeOptions, setResourceTypeOptions] = useState([]);
  const [hl7VersionOptions, setHL7VersionOptions] = useState([]);
  const [selectedHL7Version, setSelectedHL7Version] = useState(null);
  const [selectedGuide, setSelectedGuide] = useState('r4');
  const [guideOptions] = useState(contextOptions);
  const [selectedRuleVersion, setSelectedRuleVersion] = useState(null);
  const [ruleVersionOptions, setRuleVersionOptions] = useState([]);
  const [latestRuleVersion, setLatestRuleVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validationRules, setValidationRules] = useState([]);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);

  const columnSort = (a, b) => {
    if (!!a.name && !!b.name) {
      return a.name.localeCompare(b.name);
    }

    return null;
  };

  const getHL7VersionOptions = async () => {
    try {
      const { data: versions } = await API.get('/hl7-validation-rules/hl7-versions');
      setHL7VersionOptions(versions);
      setSelectedHL7Version(versions[0]);
    } catch (e) {
      handleError(e);
    }

    setLoading(false);
  };

  const getResourceTypesForHL7Version = async () => {
    if (selectedHL7Version) {
      try {
        const { data: resourceTypes } = await API.get(
          `/hl7-validation-rules/resourcetypes?hl7_version=${selectedHL7Version}`,
        );
        setResourceTypeOptions(resourceTypes);
        setSelectedResourceType(resourceTypes[0]);
      } catch (e) {
        handleError(e);
      }
    }

    setLoading(false);
  };

  const getRuleVersions = async () => {
    if (selectedHL7Version && selectedResourceType && selectedGuide) {
      try {
        const { data: versions } = await API.get(
          `/hl7-validation-rules/rule-versions?hl7_version=${selectedHL7Version}&resourcetype=${selectedResourceType}&context=${selectedGuide}`,
        );
        setRuleVersionOptions(versions);
        // endpoint returns rules sorted descending by creation date
        setSelectedRuleVersion(versions[0]);
        setLatestRuleVersion(versions[0]);
      } catch (e) {
        handleError(e);
      }
    }

    setLoading(false);
  };

  const getValidationRules = async () => {
    if (selectedHL7Version && selectedResourceType && selectedGuide && selectedRuleVersion) {
      try {
        const { data: rules } = await API.get(
          `/hl7-validation-rules?resourcetype=${selectedResourceType}&hl7_version=${selectedHL7Version}&context=${selectedGuide}&rule_version=${selectedRuleVersion}`,
        );
        setValidationRules(rules);
      } catch (e) {
        handleError(e);
      }
    }

    setLoading(false);
  };

  const handleResourceTypeDropdownSelect = resourceType => {
    if (selectedResourceType !== resourceType) {
      setLoading(true);
      setSelectedResourceType(resourceType);
    }
  };

  const handleVersionDropdownSelect = version => {
    if (selectedHL7Version !== version) {
      setLoading(true);
      setSelectedHL7Version(version);
    }
  };

  const handleGuideDropdownSelect = guide => {
    if (selectedGuide !== guide) {
      setLoading(true);
      setSelectedGuide(guide);
    }
  };

  const handleRuleVersionDropdownSelect = version => {
    if (selectedRuleVersion !== version) {
      setLoading(true);
      setSelectedRuleVersion(version);
    }
  };

  const createBooleanTag = bool => {
    const tagText = String(bool);
    const tagColor = bool ? 'green' : 'red';
    return <Tag color={tagColor}>{tagText}</Tag>;
  };

  useEffect(() => {
    setLoading(true);
    getHL7VersionOptions();
  }, []);

  useEffect(() => {
    setResourceTypeOptions([]);
    setSelectedResourceType(null);
    setLoading(true);
    getResourceTypesForHL7Version();
  }, [selectedHL7Version]);

  useEffect(() => {
    setValidationRules(null);
    setRuleVersionOptions([]);
    setSelectedRuleVersion(null);
    setLatestRuleVersion(null);
    setLoading(true);
    getRuleVersions();
  }, [selectedHL7Version, selectedResourceType, selectedGuide]);

  useEffect(() => {
    setLoading(true);
    getValidationRules();
  }, [selectedRuleVersion]);

  return (
    <>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="HL7v2 Validation Rules"
      />
      {(checkPermission(context.userData.permissions, 'editHL7ValidationRules') ||
        process.env.REACT_APP_CICD) && (
        <Row>
          <Col style={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
            <Button
              onClick={() => setShowNewVersionModal(true)}
              data-cy="validation-rules-new-version-button"
            >
              Create New Version
            </Button>
          </Col>
        </Row>
      )}
      <CreateValidationRulesModal
        closeModal={() => setShowNewVersionModal(false)}
        isOpen={showNewVersionModal}
        hl7VersionOptions={hl7VersionOptions}
        contextOptions={contextOptions}
        setParentHL7Version={setSelectedHL7Version}
        setParentResourceType={setSelectedResourceType}
        setParentContext={setSelectedGuide}
        setParentRuleVersion={setSelectedRuleVersion}
      />
      <Row>
        <Col style={{ marginTop: 10, marginRight: 10 }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={hl7VersionOptions}
                handleMenuClick={e => handleVersionDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-version-data-guide">
              <b>Hl7v2 Version: </b>
              {selectedHL7Version} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
        <Col style={{ marginTop: 10, marginRight: 10 }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={resourceTypeOptions}
                handleMenuClick={e => handleResourceTypeDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-resource-type-data-guide">
              <b>Resource Type: </b>
              {selectedResourceType} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
        <Col style={{ marginTop: 10, marginRight: 10 }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={guideOptions}
                handleMenuClick={e => handleGuideDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-implementation-guide-validation-rules">
              <b>Implementation Guide: </b>
              {selectedGuide} <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
        <Col style={{ alignSelf: 'flex-end', marginLeft: 'auto' }}>
          <Dropdown
            trigger={['click']}
            overlay={
              <DropdownMenu
                options={ruleVersionOptions}
                handleMenuClick={e => handleRuleVersionDropdownSelect(e.key)}
              />
            }
          >
            <Button data-cy="select-rule-version-validation-rules">
              <b>Rule Version: </b>
              {selectedRuleVersion && selectedRuleVersion === latestRuleVersion
                ? `${selectedRuleVersion} (latest)`
                : selectedRuleVersion}
              <DownOutlined />
            </Button>
          </Dropdown>
        </Col>
      </Row>
      <Table
        style={{ marginTop: 10 }}
        columns={[
          {
            title: 'Segment',
            dataIndex: 'segment',
            sorter: columnSort,
            width: '12.5%',
          },
          {
            title: 'Field',
            dataIndex: 'field',
            sorter: columnSort,
            width: '12.5%',
          },
          {
            title: 'Sub-Field',
            dataIndex: 'sub_field',
            width: '12.5%',
          },
          {
            title: 'Description',
            dataIndex: 'field_description',
            width: '25%',
          },
          {
            title: 'Notes',
            dataIndex: 'rule_notes',
            width: '25%',
          },
          {
            title: 'Fail Conversion',
            dataIndex: 'fail_conversion',
            render: bool => createBooleanTag(bool),
            width: '12.5%',
          },
        ]}
        rowKey="id"
        size="medium"
        loading={loading}
        dataSource={validationRules}
      />
    </>
  );
};

export default ValidationRules;
